"use client";

import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { MapPin, Star, CheckCircle, Award } from "lucide-react";

type CompaniesFiltersProps = {
  categories: Array<{ id: string; name: string; slug: string; count: number }>;
  neighborhoods: string[];
};

export default function CompaniesFilters({
  categories,
  neighborhoods,
}: CompaniesFiltersProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const selectedCategory = searchParams.get("category") || "";
  const selectedNeighborhood = searchParams.get("neighborhood") || "";
  const selectedRating = searchParams.get("rating") || "";
  const showVerified = searchParams.get("verified") === "true";
  const showFeatured = searchParams.get("featured") === "true";

  const updateParam = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete("page"); // Reset to page 1 when filtering
    router.push(`${pathname}?${params.toString()}`);
  };

  const toggleParam = (key: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (params.get(key) === "true") {
      params.delete(key);
    } else {
      params.set(key, "true");
    }
    params.delete("page");
    router.push(`${pathname}?${params.toString()}`);
  };

  const clearFilters = () => {
    router.push(pathname);
  };

  const hasActiveFilters =
    selectedCategory ||
    selectedNeighborhood ||
    selectedRating ||
    showVerified ||
    showFeatured;

  return (
    <aside className="space-y-6 px-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-mygreen hover:text-green-700 font-medium"
          >
            Limpar
          </button>
        )}
      </div>

      {/* Categoria */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
          <Award className="w-4 h-4" />
          Categoria
        </h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="radio"
              name="category"
              checked={selectedCategory === ""}
              onChange={() => updateParam("category", "")}
              className="w-4 h-4 text-mygreen focus:ring-mygreen"
            />
            <span className="text-sm text-gray-700 group-hover:text-gray-900">
              Todas
            </span>
          </label>
          {categories.map((category) => (
            <label
              key={category.id}
              className="flex items-center justify-between gap-2 cursor-pointer group"
            >
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  name="category"
                  value={category.slug}
                  checked={selectedCategory === category.slug}
                  onChange={(e) => updateParam("category", e.target.value)}
                  className="w-4 h-4 text-mygreen focus:ring-mygreen"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                  {category.name}
                </span>
              </div>
              <span className="text-xs text-gray-400">{category.count}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Localização */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          Bairro
        </h3>
        <select
          value={selectedNeighborhood}
          onChange={(e) => updateParam("neighborhood", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-mygreen/20"
        >
          <option value="">Todos os bairros</option>
          {neighborhoods.map((neighborhood) => (
            <option key={neighborhood} value={neighborhood}>
              {neighborhood}
            </option>
          ))}
        </select>
      </div>

      {/* Status */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          Status
        </h3>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={showVerified}
              onChange={() => toggleParam("verified")}
              className="w-4 h-4 text-mygreen rounded focus:ring-mygreen"
            />
            <span className="text-sm text-gray-700 group-hover:text-gray-900">
              Apenas verificadas
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={showFeatured}
              onChange={() => toggleParam("featured")}
              className="w-4 h-4 text-mygreen rounded focus:ring-mygreen"
            />
            <span className="text-sm text-gray-700 group-hover:text-gray-900">
              Em destaque
            </span>
          </label>
        </div>
      </div>
    </aside>
  );
}
